import type { Player } from '../types/player';
import { classifyTradeBalance, type ClassifiedTradeBalance } from './tradeBalance';

export type BalanceSide = 'send' | 'get';

export function playerTradeValue(player: Player): number {
  return Number(player.value ?? player.projection ?? player.projectedPoints ?? 0);
}

export function sideValue(players: Player[]): number {
  return players.reduce((sum, player) => sum + playerTradeValue(player), 0);
}

export function resolveOnBoard(roster: Player[], board: Player[]): Player[] {
  const byId = new Map(board.map((player) => [String(player.id), player]));
  return roster.map((player) => byId.get(String(player.id)) ?? player);
}

export function rankForTrade(players: Player[]): Player[] {
  return [...players].sort((a, b) => {
    const rankA = a.rank && a.rank > 0 ? a.rank : 9999;
    const rankB = b.rank && b.rank > 0 ? b.rank : 9999;
    if (rankA !== rankB) return rankA - rankB;
    return playerTradeValue(b) - playerTradeValue(a);
  });
}

export function tradeVerdict(
  youSend: Player[],
  youGet: Player[]
): ClassifiedTradeBalance & {
  you: number;
  them: number;
  ewa: number;
  gap: number;
  gapPct: number;
} {
  const you = sideValue(youGet);
  const them = sideValue(youSend);
  const avg = (you + them) / 2;
  const gap = you - them;
  const gapPct = avg > 0 ? (Math.abs(gap) / avg) * 100 : 0;
  const ewa = avg > 0 ? (gap / avg) * 0.4 : 0;
  return {
    ...classifyTradeBalance(ewa),
    you,
    them,
    ewa,
    gap,
    gapPct,
  };
}

export function evenThis(
  you: number,
  them: number,
  minGap: number = 0.45
): { side: BalanceSide; amount: number } | null {
  const amount = Math.abs(you - them);
  if (amount < minGap) return null;
  return you > them
    ? { side: 'send', amount }
    : { side: 'get', amount };
}

export function suggestReplacements(
  pool: Player[],
  targetValue: number,
  excludeIds: Array<string | number>,
  limit: number = 3
): Player[] {
  if (targetValue <= 0) return [];
  const excluded = new Set(excludeIds.map(String));
  const tolerance = Math.max(0.6, targetValue * 0.28);
  return pool
    .filter((player) => !excluded.has(String(player.id)))
    .map((player) => ({
      player,
      distance: Math.abs(playerTradeValue(player) - targetValue),
    }))
    .filter((row) => row.distance <= tolerance)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit)
    .map((row) => row.player);
}

export function suggestPackages(
  pool: Player[],
  targetValue: number,
  excludeIds: Array<string | number>,
  limit: number = 3
): Player[][] {
  if (targetValue <= 1.2) return [];
  const excluded = new Set(excludeIds.map(String));
  const tolerance = Math.max(0.7, targetValue * 0.22);
  const candidates = pool
    .filter((player) => {
      if (excluded.has(String(player.id))) return false;
      const value = playerTradeValue(player);
      return value >= 0.9 && value < targetValue - 0.4;
    })
    .sort((a, b) => playerTradeValue(b) - playerTradeValue(a))
    .slice(0, 28);

  const pairs: { players: Player[]; distance: number }[] = [];
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      const distance = Math.abs(
        playerTradeValue(candidates[i]) + playerTradeValue(candidates[j]) - targetValue
      );
      if (distance <= tolerance) {
        pairs.push({ players: [candidates[i], candidates[j]], distance });
      }
    }
  }

  const used = new Set<string>();
  return pairs
    .sort((a, b) => a.distance - b.distance)
    .filter((row) => {
      const first = String(row.players[0].id);
      const second = String(row.players[1].id);
      if (used.has(first) || used.has(second)) return false;
      used.add(first);
      used.add(second);
      return true;
    })
    .slice(0, limit)
    .map((row) => row.players);
}
